/**
 * CardSectionHeader — the title + description + trailing-control block that sits at
 * the top of an in-page Card.
 *
 * Why this exists (§36): the Analytics page grew two cards ("Scans by time" and
 * "Scans by location") that need the identical header — a title, a one-line
 * description under it (§40: descriptive copy sits directly under the title, never
 * at the bottom), and a control on the right (a status legend / a segmented toggle).
 * They had drifted before this: one titled itself `variant="h2"` and the other
 * `variant="body1"`, so two stacked cards on the same page carried different-sized
 * titles, and neither had a description at all.
 *
 * The title treatment is deliberately IDENTICAL to `@repo/ui` `Card`'s own `title`
 * prop (body1 / 20px / 400 / 140%), because that is the house card-title. This
 * component exists rather than using `Card title=` only because `Card` has no
 * description slot; the durable fix is a `description` prop on the shared `Card`,
 * at which point this becomes a thin wrapper or goes away. Until then, keep the two
 * in sync — if `Card`'s title style moves, move this with it.
 */
import React from 'react'

import { Box, Stack, Typography } from '@mui/material'

interface CardSectionHeaderProps {
  title: string
  /** One or two sentences saying what the card shows. Optional but strongly preferred. */
  description?: React.ReactNode
  /** Trailing control, right-aligned on the title's row (legend, toggle, link). */
  action?: React.ReactNode
  /**
   * A small marker sitting IMMEDIATELY after the title — a status glyph, a count.
   *
   * Distinct from `action`, and the distinction is positional rather than stylistic:
   * `action` is right-aligned to the far edge of the card, so a mark placed there
   * belongs to the ROW; this sits against the last word of the title, so it belongs to
   * the TITLE. A state marker has to be the latter — the whole reason to add one is
   * that a reader scanning a column of titles can answer "which of these are done"
   * without their eye leaving the titles.
   *
   * It is a sibling of the `h2` rather than a child of it, so the heading's accessible
   * name stays the title alone and the marker carries its own label.
   */
  titleAdornment?: React.ReactNode
}

// Mirrors @repo/ui Card's `title` Typography exactly — see the note above.
const TITLE_SX = {
  fontFamily: 'Lato, Montserrat, sans-serif',
  fontSize: '20px',
  fontWeight: 400,
  fontStyle: 'normal',
  lineHeight: '140%',
  letterSpacing: 0,
} as const

export const CardSectionHeader = ({
  title,
  description,
  action,
  titleAdornment,
}: CardSectionHeaderProps) => (
  <Stack
    direction="row"
    // Top-aligned, not centred: the left column can be two lines (title +
    // description) while the action is one control, and centring it against a
    // two-line block floats it off the title it belongs to.
    alignItems="flex-start"
    justifyContent="space-between"
    spacing={4}
  >
    <Box sx={{ minWidth: 0 }}>
      {/* `alignItems: center` against the title's own 140% line box, so a 16px glyph
          sits on the optical centre of a 20px title rather than on its baseline. */}
      <Stack direction="row" alignItems="center" spacing={2} sx={{ minWidth: 0 }}>
        <Typography variant="body1" component="h2" sx={TITLE_SX}>
          {title}
        </Typography>
        {/* `flexShrink: 0` — the TITLE gives way if the row is narrow, never the marker.
            A truncated title is still readable; half a glyph says nothing. */}
        {titleAdornment && (
          <Box sx={{ display: 'flex', flexShrink: 0 }}>{titleAdornment}</Box>
        )}
      </Stack>

      {description && (
        <Typography
          variant="body3"
          component="p"
          color="text.secondary"
          // 1.5 so a two-line description doesn't read dense (§15).
          sx={{ mt: 2, lineHeight: 1.5 }}
        >
          {description}
        </Typography>
      )}
    </Box>

    {action && <Box sx={{ flexShrink: 0 }}>{action}</Box>}
  </Stack>
)
